import React, { useState } from 'react';
import styled from 'styled-components';
import { Typography, Grid, Box, Container, useMediaQuery, Theme, TextField, Button, Select, MenuItem, Checkbox, FormControlLabel, FormGroup } from '@material-ui/core';
import envConfig from '../../../../config/environment-config';
import { patientsService } from '../../../../services';
import { inputLabelProps } from '../../common/styles';
import { ArrowRight, FileCheck } from 'mdi-material-ui';
import { useLoader } from '../../../../contexts/pharmaceutical-portal/Loader.context';
import { IMaskInputCellPhone, IMaskInputCPF } from '../../common/components/IMaskInput';
import { useAlert } from '../../../../contexts/pharmaceutical-portal/Alert.context';

const Title = styled.h4`
  font-family: Roboto;
  font-weight: 400;
  text-align: left;
  margin-bottom: 20px;
  font-size: 18px;

  @media (max-width: 768px) {
    margin-bottom: 10px;
    font-family: Roboto;
    font-weight: 400;
    text-align: left;
    font-size: 16px;
  }
`;

const specialties = [
  { label: "Tipo de especialista", value: 'unselected', isDisabled: true },
  { label: "Fisioterapeuta", value: "fisioterapeuta" },
  { label: "Psicóloga", value: "psicologa" },
  { label: "Nutricionista", value: "nutricionista" },
  { label: "Enfermeiro", value: "enfermeiro" },
  { label: "Farmacêutico", value: "farmaceutico" },
];

interface IFormData {
  idFollowupPatient: string;
  programCode: number;
  name: string;
  cpf: string;
  phone: string;
  acceptContact: boolean;
  specialty: string;
}

const defaultFormData: IFormData = {
  idFollowupPatient: '',
  programCode: Number(envConfig.boehringerProgramCode),
  name: '',
  cpf: '',
  phone: '',
  acceptContact: false,
  specialty: 'unselected',
};

export function ScheduleIndicationForm(): JSX.Element {
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const { showLoader, hideLoader } = useLoader();
  const { showAlert, hideAlert } = useAlert();
  const [step, setStep] = useState<number>(1);
  const [formData, setFormData] = useState<IFormData>(defaultFormData);

  const handleSearchPatient = () => {
    const { cpf, programCode } = formData;
    if (!cpf || cpf.length < 11) {
      showAlert('CPF  inválido', 'Por favor, informe um CPF válido', 'error');
      setTimeout(() => hideAlert(), 3000);
      return;
    }
    showLoader();
    patientsService.consultPatientCRMPatientCare({
      cpf,
      programCode,
    }).then((response) => {
      hideLoader();
      if (response.data) {
        setFormData(prevFormData => ({
          ...prevFormData,
          idFollowupPatient: response.data.idFollowUpPatient,
          name: response.data.name,
          phone: response.data.mainPhone,
        }));
        setStep(2);
      } else {
        showAlert('Paciente não encontrado no programa', 'Por favor, verifique o CPF informado', 'error');
        setTimeout(() => hideAlert(), 3000);
      }
    }).catch((error) => {
      hideLoader();
      if (error?.response?.data?.message == 'Paciente não encontrado no programa.') {
        showAlert('Erro', error?.response?.data?.message, 'error');
      } else {
        showAlert('Ocorreu um erro ao consultar o paciente', 'Por favor, tente novamente', 'error');
      }
      setTimeout(() => hideAlert(), 3000);
    });
  };

  const handleRequestSpecialtyIndication = () => {
    const { programCode, idFollowupPatient, name, cpf, phone, acceptContact, specialty } = formData;
    if (!programCode || !idFollowupPatient || !name || !cpf || !phone || !acceptContact || !specialty || specialty === 'unselected') {
      showAlert('Atenção', 'Por favor, preencha todos os campos obrigatórios', 'warning');
      setTimeout(() => hideAlert(), 3000);
      return;
    }
    showLoader();
    patientsService.createSpecialtyIndication({
      programCode,
      idFollowupPatient,
      name,
      cpf,
      phone,
      acceptSMS: acceptContact,
      acceptCall: acceptContact,
      acceptEmail: acceptContact,
      acceptWhatsApp: acceptContact,
      specialty,
    }).then(() => {
      hideLoader();
      setStep(3);
    }).catch((error) => {
      hideLoader();
      
      const errorMessage = error?.response?.data?.message || 'Ocorreu um erro ao realizar o encaminhamento';
  
      console.error('Erro ao criar indicação de especialidade:', errorMessage);

      showAlert(
        'Ocorreu um erro ao realizar o encaminhamento',
        errorMessage,
        'error'
      );
      
      setTimeout(() => hideAlert(), 3000);
    });
  };

  const handleNewIndication = () => {
    setFormData(defaultFormData);
    setStep(1);
  }

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, propertyName: string) => {
    let { value } = event.target;
    const checked  = (event.target as HTMLInputElement).checked || false;

    // Unmask value for specific fields
    if (propertyName === 'cpf' || propertyName === 'phone') {
      value = value.replace(/\D/g, ''); // Removes all non-digit characters
    }

    setFormData(prevFormData => ({
      ...prevFormData,
      [propertyName]: propertyName == 'acceptContact' ? checked : value,
    }));
  }

  const handleSpecialtyChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const specialty = event.target.value as string;
    
    setFormData(prevFormData => ({
      ...prevFormData,
      specialty: specialty,
    }));
  };

  return (
    <Container>
      <Box my={2}>
        <Grid container spacing={2}>
          <Typography variant="h5" style={{ fontWeight: 'bold', fontSize: '1.8rem', textAlign: 'left', width: '100%' }}>
            Especialistas
          </Typography>
          <hr style={{ border: '1px solid lightgray', margin: '16px 0 16px 0', width: '100%' }} />
          
          {step === 1 && (
            <>
              <Title>
                Informe o cpf do paciente que deseja encaminhar para um especialista.
              </Title>
              <Grid container spacing={2} style={{ marginBottom: '20px' }}>
                <Grid item xs={12} lg={8}>
                  <TextField
                    label="CPF do paciente"
                    variant="outlined"
                    fullWidth
                    value={formData.cpf}
                    onChange={(e) => handleInputChange(e, 'cpf')}
                    InputLabelProps={{ ...inputLabelProps, shrink: !!formData.cpf }}
                    InputProps={{
                      inputComponent: IMaskInputCPF as any,
                    }}
                  />
                </Grid>
                <Grid item xs={12} lg={4}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSearchPatient}
                    size="large"
                    fullWidth
                    style={!isMobile ? { height: '100%', marginTop: '-2px' } : {}}
                  >
                    Próximo <ArrowRight style={{ marginLeft: '8px' }} />
                  </Button>
                </Grid>
              </Grid>
            </>
          )}

          {step === 2 && (
            <Grid container spacing={2} style={{ marginBottom: '20px' }}>
              <Grid item xs={12} md={6} lg={6}>
                <TextField
                  label="Nome completo do paciente"
                  variant="outlined"
                  fullWidth
                  value={formData.name}
                  onChange={(e) => handleInputChange(e, 'name')}
                  InputLabelProps={inputLabelProps}
                />
              </Grid>
              <Grid item xs={12} md={6} lg={6}>
                <TextField
                  label="CPF do paciente"
                  variant="outlined"
                  fullWidth
                  value={formData.cpf}
                  InputLabelProps={{ ...inputLabelProps, shrink: !!formData.cpf }}
                  InputProps={{
                    inputComponent: IMaskInputCPF as any,
                  }}
                  disabled
                />
              </Grid>
              <Grid item xs={12} md={6} lg={6}>
                <TextField
                  label="Telefone do paciente"
                  variant="outlined"
                  fullWidth
                  value={formData.phone}
                  onChange={(e) => handleInputChange(e, 'phone')}
                  InputLabelProps={{ ...inputLabelProps, shrink: !!formData.phone }}
                  InputProps={{
                    inputComponent: IMaskInputCellPhone as any,
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6} lg={6}>
                <Select
                  value={formData.specialty}
                  onChange={handleSpecialtyChange}
                  variant="outlined"
                  style={{
                    width: '100%',
                    height: '56px',
                    fontSize: '1rem'
                  }}
                >
                  {specialties.map((specialty, index) => (
                    <MenuItem key={index} value={specialty.value} disabled={specialty.isDisabled}>
                      <Typography variant="body1">{specialty.label}</Typography>
                    </MenuItem>
                  ))}
                </Select>
              </Grid>
              <Grid item xs={12} md={12} lg={12}>
                <FormGroup>
                  <FormControlLabel
                    control={<Checkbox required onChange={(e) => handleInputChange(e, 'acceptContact')} />}
                    label="O paciente autoriza que o programa entre em contato"
                  />
                </FormGroup>
              </Grid>
              <Grid item xs={12} md={6} lg={6}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleRequestSpecialtyIndication}
                  size="large"
                  fullWidth
                  style={!isMobile ? { height: '100%', marginTop: '-2px' } : {}}
                >
                  Fazer indicação
                </Button>
              </Grid>
            </Grid>
          )}

          {step === 3 && (
            <Grid container spacing={2} style={{ marginTop: '20px', marginBottom: '20px' }}>
              <Grid item xs={12} lg={12}>
                <Grid container justify='center' alignItems='center' alignContent='center' spacing={2}>
                  <Grid item xs={6} lg={6}>
                    <Box
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      style={{
                        width: '80px',
                        height: '80px',
                        borderRadius: '50%',
                        backgroundColor: '#E8F5E9', // Light green background
                        margin: '0 auto 16px auto', // Center horizontally, add bottom margin
                      }}
                    >
                      <FileCheck style={{ fontSize: '3rem', color: '#4CAF50' }} /> {/* Green icon */}
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} lg={12}>
                <Grid container justify='center' alignItems='center' alignContent='center' spacing={2}>
                  <Grid item xs={6} lg={6}>
                    <Typography variant="h5" style={{ fontWeight: 'bold', fontSize: '1.8rem', textAlign: 'center', width: '100%', marginBottom: '8px' }}>
                      Paciente encaminhado com <span style={{ color: '#4CAF50' }}>sucesso!</span>
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} lg={12}>
                <Grid container justify='center' alignItems='center' alignContent='center' spacing={2}>
                  <Grid item xs={6} lg={6}>
                    <Title style={{ textAlign: 'center', color: '#757575', fontSize: '1rem', marginBottom: '24px', lineHeight: '1.5' }}>
                      Em breve, um especialista entrará em contato com o paciente para agendar a consulta.
                    </Title>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} lg={12}>
                <Grid container justify='center' alignItems='center' alignContent='center' spacing={2}>
                  <Grid item xs={6} md={6} lg={6}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleNewIndication}
                      size="large"
                      fullWidth
                      style={!isMobile ? { height: '100%', marginTop: '-2px' } : {}}
                    >
                      Fazer outra indicação
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          )}

        </Grid>
      </Box>
    </Container>
  );
}
